/**
 * 合规档案 - PRD 04 M10 / 07 §5
 * 一屏看全：主体资质 + 合作合同 + 订单四流证据汇总 + 对账报告 + 哈希链校验
 * 这是应对税务核查的核心页面
 */

import { useState } from 'react'
import { Card, Table, Badge, Button, useApi, money, time } from '../../components/ui'
import { OrderDetailModal } from '../../components/OrderDetailModal'
import { HelpBubble } from '../../components/HelpBubble'
import { api, getIdentity } from '../../api/client'

interface Merchant {
  merchant_id: string
  company_name: string
  type: string
  credit_code: string
  legal_person: string
  tax_no: string
  register_region: string
  status: string
  created_at: string
}
interface Partnership {
  partnership_id: string
  factory_id: string
  commission_rate: string
  status: string
  signed_at: string
}
interface Order {
  order_id: string
  amount_total: string
  status: string
  synced_at: string
}
interface Invoice {
  invoice_id: string
  invoice_category: string
  issuer_role: string
  amount: string
  tax_rate: string
  remark: string
  invoice_no: string
  status: string
}

export function SellerCompliance() {
  const identity = getIdentity()
  const me = useApi<Merchant>(async () => api.get('/merchants/me'), [])
  const partnerships = useApi<Partnership[]>(async () => api.get('/partnerships'), [])
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const invoices = useApi<Invoice[]>(async () => api.get('/invoices'), [])
  const [orderDetail, setOrderDetail] = useState<string | null>(null)

  // GMV = 所有已支付订单（含已退款的，退款也是历史 GMV）
  const settledOrders = (orders.data ?? []).filter((o) => ['PAID', 'REFUNDED'].includes(o.status))
  const totalGmv = settledOrders.reduce((s, o) => s + parseFloat(o.amount_total), 0)

  if (me.loading || !me.data) return <div style={{ padding: 24, color: '#888' }}>加载中...</div>

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>合规档案</h2>
      <div style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
        本档案汇集您的全部合规证据，供税务机关核查。所有数据均经哈希链存证，不可篡改。
      </div>

      {/* ① 主体资质 */}
      <Card title="① 主体资质">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 13 }}>
          <KV k="企业名称" v={me.data.company_name} />
          <KV k="主体ID" v={me.data.merchant_id} />
          <KV k="身份" v={me.data.type === 'FACTORY' ? '工厂（委托方）' : '商家（受托方）'} />
          <KV k="统一信用代码" v={me.data.credit_code} />
          <KV k="法人" v={me.data.legal_person} />
          <KV k="税号" v={me.data.tax_no} />
          <KV k="入驻时间" v={time(me.data.created_at)} />
          <KV k="状态" v={me.data.status} />
        </div>
      </Card>

      {/* ② 委托代销合同 */}
      <Card title={`② 委托代销合同（${partnerships.data?.length ?? 0} 份，均已上链存证）`}>
        <Table<Partnership>
          rows={partnerships.data ?? []}
          empty="暂无合同"
          columns={[
            { key: 'partnership_id', title: '合作ID' },
            { key: 'factory_id', title: '工厂' },
            {
              key: 'commission_rate',
              title: '佣金率',
              render: (r) => `${(parseFloat(r.commission_rate) * 100).toFixed(1)}%`,
            },
            { key: 'signed_at', title: '签署时间', render: (r) => (r.signed_at ? time(r.signed_at) : '-') },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
          ]}
        />
      </Card>

      {/* ③ 订单与四流证据汇总 */}
      <Card title={`③ 订单证据汇总（累计 GMV ${money(totalGmv)}，${settledOrders.length} 笔已分账）`}>
        <Table<Order>
          rows={orders.data ?? []}
          empty="暂无订单"
          columns={[
            { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
            { key: 'amount_total', title: '金额', render: (r) => money(r.amount_total) },
            { key: 'synced_at', title: '时间', render: (r) => time(r.synced_at) },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
          ]}
        />
      </Card>

      {/* ④ 发票汇总（双票）*/}
      <Card title={`④ 发票汇总（双票，${invoices.data?.length ?? 0} 张）`}>
        <Table<Invoice>
          rows={invoices.data ?? []}
          empty="暂无发票"
          columns={[
            {
              key: 'invoice_category',
              title: '类别',
              render: (r) => (
                <Badge
                  status="ISSUED"
                  label={r.invoice_category === 'GOODS' ? '商品票(13%)' : '服务费票(6%)'}
                />
              ),
            },
            {
              key: 'issuer_role',
              title: '销售方',
              render: (r) => (r.issuer_role === 'FACTORY' ? '工厂' : '商家'),
            },
            { key: 'amount', title: '金额', render: (r) => money(r.amount) },
            { key: 'invoice_no', title: '发票号' },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
          ]}
        />
      </Card>

      {/* ⑤ 哈希链自证 */}
      <HashChainSelfProof />

      <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 6, fontSize: 12, color: '#166534' }}>
        ✓ 上述全部数据（合同、订单、分账、发票、物流）均通过哈希链存证，任何篡改都会在校验时暴露。
        切真实接口后，可一键导出 PDF 完整档案提交税务机关。
      </div>

      <HelpBubble pageKey="seller-compliance" title="合规档案">
        <p>这是应对税务核查的核心页面：一屏看全主体资质、合作合同、订单四流证据、哈希链自证。</p>
        <p>所有数据（合同、订单、分账、发票、物流）均通过哈希链存证，任何篡改都会在校验时暴露。</p>
      </HelpBubble>

      {orderDetail && <OrderDetailModal orderId={orderDetail} onClose={() => setOrderDetail(null)} />}
    </div>
  )
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: 11 }}>{k}</div>
      <div style={{ fontWeight: 500 }}>{v}</div>
    </div>
  )
}

/** 哈希链自证：取自己相关的存证，逐条校验 */
function HashChainSelfProof() {
  const list = useApi<
    Array<{ evidence_id: string; entity_type: string; entity_id: string; flow_type: string; chain_hash: string; timestamp: string }>
  >(async () => api.get('/admin/chain?limit=20'), [])
  const [result, setResult] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState(false)

  async function verifyAll() {
    setBusy(true)
    const r: Record<string, boolean> = {}
    for (const e of list.data ?? []) {
      try {
        const v = await api.post<{ valid: boolean }>('/admin/chain/verify', { evidenceId: e.evidence_id })
        r[e.evidence_id] = v.valid
      } catch {
        r[e.evidence_id] = false
      }
    }
    setResult(r)
    setBusy(false)
  }

  const verified = Object.keys(result).length
  const allOk = verified > 0 && Object.values(result).every(Boolean)

  return (
    <Card
      title="⑤ 哈希链自证（防篡改）"
      action={
        <Button size="sm" onClick={verifyAll} disabled={busy || !list.data?.length}>
          {busy ? '校验中...' : verified > 0 ? '重新校验' : '校验全部存证'}
        </Button>
      }
    >
      {verified > 0 && (
        <div style={{ marginBottom: 12, padding: 10, background: allOk ? '#f0fdf4' : '#fef2f2', borderRadius: 4, fontSize: 13 }}>
          {allOk ? `✓ ${verified} 条存证全部未篡改` : `✗ 检测到篡改，请联系平台`}
        </div>
      )}
      <Table
        rows={list.data ?? []}
        empty="暂无存证"
        columns={[
          { key: 'flow_type', title: '流类型', render: (r: any) => flowLabel(r.flow_type) },
          { key: 'entity_type', title: '实体' },
          { key: 'entity_id', title: '实体ID', render: (r: any) => r.entity_id.slice(0, 20) + '...' },
          { key: 'timestamp', title: '时间', render: (r: any) => time(r.timestamp) },
          {
            key: 'verify',
            title: '校验',
            render: (r: any) => {
              const v = result[r.evidence_id]
              if (v === undefined) return <span style={{ color: '#888' }}>-</span>
              return v ? <span style={{ color: '#166534' }}>✓ 未篡改</span> : <span style={{ color: '#dc2626' }}>✗ 篡改</span>
            },
          },
        ]}
      />
    </Card>
  )
}

function flowLabel(f: string): string {
  const m: Record<string, string> = {
    CONTRACT_FLOW: '合同流',
    FUND_FLOW: '资金流',
    INVOICE_FLOW: '发票流',
    LOGISTICS_FLOW: '物流流',
  }
  return m[f] ?? f
}
